import React from 'react';
import { Container, Grid, Paper, Typography, Box, LinearProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';

/**
 * My Progress Page
 * Compact progress cards with performance metrics
 */
const MyProgress = () => {
  const progressData = {
    orders: {
      total: 25,
      fulfilled: 18,
      pending: 5,
      cancelled: 2,
      fulfillmentRate: 72
    },
    quotations: {
      total: 20,
      converted: 12,
      pending: 6,
      lost: 2,
      conversionRate: 60
    },
    enquiries: {
      total: 30,
      converted: 8,
      inProgress: 15,
      noResponse: 7,
      conversionRate: 27
    }
  };

  const ProgressCard = ({ title, data, color }) => (
    <Paper 
      sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        height: '100%',
        borderRadius: { xs: 1, sm: 2 },
        boxShadow: { xs: 1, sm: 2 },
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: { xs: 1, sm: 4 },
        }
      }}
    >
      <Typography variant="h6" sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.9375rem', sm: '1rem' }, fontWeight: 600 }}>
        {title}
      </Typography>
      <Box sx={{ mt: { xs: 1, sm: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, sm: 1.5 } }}>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' }, fontWeight: 500 }}>
            Total: {data.total}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' }, fontWeight: 600 }} color={`${color}.main`}>
            {title === 'Orders' && `${data.fulfillmentRate}% Fulfilled`}
            {title === 'Quotations' && `${data.conversionRate}% Converted`}
            {title === 'Enquiries' && `${data.conversionRate}% Converted`}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={title === 'Orders' ? data.fulfillmentRate : data.conversionRate}
          sx={{ 
            height: 8, 
            borderRadius: 4, 
            mb: 2,
            backgroundColor: 'action.hover',
          }}
          color={color === 'primary' ? 'primary' : color === 'success' ? 'success' : 'warning'}
        />
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          {Object.entries(data).filter(([key]) => key !== 'total' && key !== 'fulfillmentRate' && key !== 'conversionRate').map(([key, value]) => (
            <Grid item xs={6} key={key}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {key === 'fulfilled' || key === 'converted' ? (
                  <CheckCircleIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
                ) : key === 'cancelled' || key === 'lost' ? (
                  <CancelIcon sx={{ fontSize: '1rem', color: 'error.main' }} />
                ) : (
                  <PendingIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                )}
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}>
                  {key.charAt(0).toUpperCase() + key.slice(1)}: {value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Paper>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1, sm: 2 } }}>
      <Typography variant="h5" sx={{ mb: { xs: 2, sm: 2.5 }, fontSize: { xs: '1.125rem', sm: '1.25rem' }, fontWeight: 600 }}>
        My Progress
      </Typography>

      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        <Grid item xs={12} md={4}>
          <ProgressCard
            title="Orders"
            data={progressData.orders}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ProgressCard
            title="Quotations"
            data={progressData.quotations}
            color="success"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ProgressCard
            title="Enquiries"
            data={progressData.enquiries}
            color="warning"
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2.5 }}>
        <Paper 
          sx={{ 
            p: 2.5,
            borderRadius: 2,
            boxShadow: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" sx={{ mb: { xs: 0.75, sm: 1 }, fontSize: { xs: '0.9375rem', sm: '1rem' }, fontWeight: 600 }}>
            Performance Summary
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
            Track your performance across orders, quotations, and enquiries to identify areas for improvement
            and celebrate your successes.
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default MyProgress;
